import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import type {
  GetMaintenanceCategoriesInput,
  MaintenanceCategoryWithNavigationPropertiesDto,
} from '../../../proxy/maintenance-categories/models';
import { MaintenanceCategoryService } from '../../../proxy/maintenance-categories/maintenance-category.service';

@Component({
  selector: 'lib-maintenance-category',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './maintenance-category.component.html',
  styles: [],
})
export class MaintenanceCategoryComponent implements OnInit {
  data: PagedResultDto<MaintenanceCategoryWithNavigationPropertiesDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetMaintenanceCategoriesInput;

  form: FormGroup;

  isFiltersHidden = true;

  isModalBusy = false;

  isModalOpen = false;

  selected?: MaintenanceCategoryWithNavigationPropertiesDto;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: MaintenanceCategoryService,
    private confirmation: ConfirmationService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<MaintenanceCategoryWithNavigationPropertiesDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  clearFilters() {
    this.filters = {} as GetMaintenanceCategoriesInput;
  }

  buildForm() {
    const {
      name,
      description,
      procedureId,
    } = this.selected?.maintenanceCategory || {};

    this.form = this.fb.group({
      name: [name ?? null, [Validators.required]],
      description: [description ?? null, []],
      procedureId: [procedureId ?? null, []],
    });
  }

  hideForm() {
    this.isModalOpen = false;
    this.form.reset();
  }

  showForm() {
    this.buildForm();
    this.isModalOpen = true;
  }

  submitForm() {
    if (this.form.invalid) return;

    const request = this.selected
      ? this.service.update(this.selected.maintenanceCategory.id, this.form.value)
      : this.service.create(this.form.value);

    this.isModalBusy = true;

    request
      .pipe(
        finalize(() => (this.isModalBusy = false)),
        tap(() => this.hideForm()),
      )
      .subscribe(this.list.get);
  }

  create() {
    this.selected = undefined;
    this.showForm();
  }

  update(record: MaintenanceCategoryWithNavigationPropertiesDto) {
    this.selected = record;
    this.showForm();
  }

  delete(record: MaintenanceCategoryWithNavigationPropertiesDto) {
    this.confirmation.warn(
      'Inventory::DeleteConfirmationMessage',
      'Inventory::AreYouSure',
      { messageLocalizationParams: [] }
    ).pipe(
      filter(status => status === Confirmation.Status.confirm),
      switchMap(() => this.service.delete(record.maintenanceCategory.id)),
    ).subscribe(this.list.get);
  }
}
